import { useEffect, useRef, useState } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Spinner } from '@/components/ui';
import { useApi } from '@/hooks/useApi';
import type { Fit } from '@/lib/types';

interface FitPhotoLightboxProps {
  /** Fit to show photos for; null closes the lightbox. */
  fitId: string | null;
  onClose: () => void;
}

// Fullscreen photo viewer for a fit: every fit/wear photo, falling back to the
// piece photos when the fit has no photo of its own.
export function FitPhotoLightbox({ fitId, onClose }: FitPhotoLightboxProps) {
  const { data, loading } = useApi<{ fit: Fit }>(fitId ? `/fits/${fitId}` : null);
  const [index, setIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);

  const fit = data?.fit;
  const photos: string[] =
    fit?.photos && fit.photos.length > 0
      ? fit.photos.map((p) => p.url)
      : fit?.imageUrl
        ? [fit.imageUrl]
        : (fit?.piecePhotoUrls ?? []);

  // Start from the first photo whenever a different fit is opened.
  useEffect(() => {
    setIndex(0);
  }, [fitId]);

  const prev = () => setIndex((i) => (i - 1 + photos.length) % photos.length);
  const next = () => setIndex((i) => (i + 1) % photos.length);

  useEffect(() => {
    if (!fitId) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (photos.length > 1 && e.key === 'ArrowLeft') prev();
      if (photos.length > 1 && e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitId, photos.length, onClose]);

  if (!fitId) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/90 flex flex-col items-center justify-center p-4"
      onClick={onClose}
      onTouchStart={(e) => {
        touchStartX.current = e.touches[0].clientX;
      }}
      onTouchEnd={(e) => {
        if (touchStartX.current === null || photos.length < 2) return;
        const dx = e.changedTouches[0].clientX - touchStartX.current;
        touchStartX.current = null;
        if (dx > 40) prev();
        else if (dx < -40) next();
      }}
    >
      <button
        className="absolute top-4 right-4 p-2 text-white/80 hover:text-white"
        onClick={onClose}
        aria-label="Close"
      >
        <X className="w-6 h-6" />
      </button>

      {loading ? (
        <Spinner className="w-8 h-8 text-white" />
      ) : photos.length === 0 ? (
        <p className="text-sm text-white/70">No photos for this fit yet.</p>
      ) : (
        <>
          <img
            src={photos[index]}
            alt={fit?.name ?? 'Fit photo'}
            className="max-w-full max-h-[80vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="mt-3 flex items-center gap-4 text-white/80" onClick={(e) => e.stopPropagation()}>
            {photos.length > 1 && (
              <button onClick={prev} className="p-2 hover:text-white" aria-label="Previous photo">
                <ChevronLeft className="w-6 h-6" />
              </button>
            )}
            <span className="text-xs tabular-nums">
              {fit?.name}
              {photos.length > 1 ? ` · ${index + 1} / ${photos.length}` : ''}
            </span>
            {photos.length > 1 && (
              <button onClick={next} className="p-2 hover:text-white" aria-label="Next photo">
                <ChevronRight className="w-6 h-6" />
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
